// src/features/patient/care-treatment/utils/careTimelinePolling.ts

/**
 * Purpose : How often Care & Treatment re-checks while scanned pages are
 * still being read in the background. Each check is a conditional GET (the
 * ETag): an unchanged timeline is a cheap 304. Backoff: the first check after
 * 10 s, the next 30 s later, then every 60 s, stopping 15 minutes after the
 * wait began; the screen then says "still processing, check back later".
 *
 * Contents :
 *
 * * const(POLL_LIMIT_MS)
 * * function(isWaitingForText) : anything still waiting for its text
 * * function(pollDelay) : the next check's delay for a wait that began at startedAt
 * * function(nextPoll) / function(pollingGaveUp) / function(pollingEndsAt) : per-profile wait tracking
 */

import type { CareTimelineResponse } from "@/features/patient/care-treatment/api/careTimelineApi";

export const POLL_LIMIT_MS = 15 * 60_000;

// function(isWaitingForText)
export function isWaitingForText(data: CareTimelineResponse | undefined): boolean {
  return Boolean(data?.notes.some((n) => n.bodyPending) || data?.documents?.some((d) => d.kind === "pending"));
}

// function(pollDelay)
/**
 * 10 s, then 30 s, then 60 s apart, from elapsed time alone (so it's the same
 * however often the query library asks); false once POLL_LIMIT_MS has passed.
 */
export function pollDelay(startedAt: number, now: number = Date.now()): number | false {
  const elapsed = now - startedAt;
  if (elapsed >= POLL_LIMIT_MS) return false;
  if (elapsed < 10_000) return 10_000;
  if (elapsed < 40_000) return 30_000;
  return 60_000;
}

// When each profile's current wait began (cleared once nothing is waiting).
const waits = new Map<string, number>();

// function(nextPoll)
/** The query's refetchInterval: the next check's delay, or false (nothing waiting / gave up). */
export function nextPoll(key: string, data: CareTimelineResponse | undefined, now: number = Date.now()): number | false {
  if (!isWaitingForText(data)) {
    waits.delete(key);
    return false;
  }
  if (!waits.has(key)) waits.set(key, now);
  return pollDelay(waits.get(key) as number, now);
}

// function(pollingGaveUp)
/** Something is still waiting and the checks have stopped: show "check back later". */
export function pollingGaveUp(key: string, data: CareTimelineResponse | undefined, now: number = Date.now()): boolean {
  const startedAt = waits.get(key);
  return isWaitingForText(data) && startedAt !== undefined && now - startedAt >= POLL_LIMIT_MS;
}

/** When the current wait for `key` stops being checked, or undefined. */
export function pollingEndsAt(key: string): number | undefined {
  const startedAt = waits.get(key);
  return startedAt === undefined ? undefined : startedAt + POLL_LIMIT_MS;
}
